import classNames from 'classnames';
import CitationList from 'modules/References/components/CitationList';
import * as React from 'react';
import AssetListFilter from 'components/AssetListFilter';
import ElementList from 'components/ElementList';
import ImageList from 'components/ImageList';
import InteractiveElements from 'components/InteractiveElements';
import ReusableLayoutList from 'components/LayoutList';
import LayoutListContainer from 'components/LayoutListContainer';
import ModuleBundleList from 'components/ModuleBundleList';
import RefreshPanel from 'components/RefreshPanel';
import StoryCardList from 'components/StoryCardList';
import TextCollectionList from 'components/TextCollectionList';
import TextComponentList from 'components/TextComponentList';
import { AssetFilter, EntityType, ProjectPanelSection, ProjectPanelTab } from 'const';
import * as Models from 'models';
import { intlGet } from 'utils/intlGet';
import { IProjectPanelState, ProjectPanelProps } from './models';
import Styles from './styles.module.scss';

const AssetFilterByEntityType: PartialRecord<EntityType, AssetFilter> = {
  [EntityType.LAYOUT]: AssetFilter.LAYOUTS,
  [EntityType.GROUP_LAYOUT]: AssetFilter.GROUP_LAYOUTS,
};

export default class ProjectPanel extends React.PureComponent<ProjectPanelProps, IProjectPanelState> {
  readonly state: IProjectPanelState = {
    assetFilters: [],
    filterExpanded: false,
  };

  private readonly setActiveTab = (tab: ProjectPanelTab): void => {
    this.props.setActiveTab(tab);
  };

  private readonly setAssetFilters = (assetFilters: string[]): void => {
    this.setState({ assetFilters });
  };

  private readonly setContentFilters = (contentFilters: string[]): void => {
    this.props.setContentFilter(contentFilters);
  };

  private readonly toggleFilterExpanded = (): void => {
    this.setState(prevState => ({
      filterExpanded: !prevState.filterExpanded,
    }));
  };

  private readonly shouldDisplayAsset = (asset: AssetFilter): boolean => {
    const { assetFilters } = this.state;

    return assetFilters.length === 0 || assetFilters.includes(asset);
  };

  private readonly getLayoutsToShow = (): Models.CombinedLayoutDocumentsMap => {
    const { reusableLayouts } = this.props;
    const { assetFilters } = this.state;

    if (assetFilters.length === 0) {
      return reusableLayouts;
    }

    return reusableLayouts.filter(document => assetFilters.includes(AssetFilterByEntityType[document.get('entityType')]));
  };

  render(): React.ReactNode {
    const { filterExpanded } = this.state;
    const {
      activeSections,
      activeTab,
      availableFilters,
      citations,
      images,
      moduleBundles,
      showUpdatedModuleBundles,
      showUpdatedStoryCards,
      showUpdatedTextCollections,
      storyCardsAndMagicForms,
      textCollections,
      textComponents,
      toggleSection,
      toggleUpdatedDocumentsDisplaying,
      updatedInPromoMatsEntities,
      isReadOnly,
    } = this.props;

    const assetsWrapperDataTestId = 'projectPanel-listsContainer-assets-contentWrapper';

    return (
      <div className={Styles.ProjectPanel} data-testid="projectPanel">
        <div className={Styles.tabs__wrapper} data-testid="projectPanel-tabsContainer">
          <ul className={Styles.tabs} data-testid="projectPanel-tabsContainer-tabs">
            <li className={classNames({ [Styles.active]: activeTab === ProjectPanelTab.ASSETS })}
              data-testid="projectPanel-tabsContainer-tabs-assets">
              <button
                value={ProjectPanelTab.ASSETS}
                onClick={(): void => this.setActiveTab(ProjectPanelTab.ASSETS)}
                data-testid="projectPanel-tabsContainer-tabs-assets-button"
              >
                {intlGet('ProjectPanel.Label', 'Assets')}
              </button>
            </li>
            { !isReadOnly && <li className={classNames({ [Styles.active]: activeTab === ProjectPanelTab.ELEMENTS })}
              data-testid="projectPanel-tabsContainer-tabs-elements"
            >
              <button
                value={ProjectPanelTab.ELEMENTS}
                onClick={(): void => this.setActiveTab(ProjectPanelTab.ELEMENTS)}
                data-testid="projectPanel-tabsContainer-tabs-elements-button"
              >
                {intlGet('ProjectPanel.Label', 'Elements')}
              </button>
            </li>}
          </ul>
        </div>
        <div className={Styles.container} data-testid="projectPanel-listsContainer">
          {
            <>
              { !isReadOnly &&
              <div className={classNames(Styles.filter__wrapper, { [Styles.show__flex]: activeTab === ProjectPanelTab.ASSETS })}
                data-testid="projectPanel-listsContainer-assets-filterWrapper"
              >
                <AssetListFilter
                  activeTab={activeTab}
                  availableFilters={availableFilters}
                  setAssetFilters={this.setAssetFilters}
                  setContentFilters={this.setContentFilters}
                  toggleFilterExpanded={this.toggleFilterExpanded}
                />
              </div> }
              <div
                className={classNames(Styles.content__wrapper, {
                  [Styles.short]: filterExpanded,
                  [Styles.show]: activeTab === ProjectPanelTab.ASSETS,
                })}
                data-testid={assetsWrapperDataTestId}
              >
                {
                  this.shouldDisplayAsset(AssetFilter.MODULE_BUNDLES) &&
                  <ModuleBundleList
                    items={moduleBundles}
                    showUpdatedModuleBundles={showUpdatedModuleBundles}
                    toggleUpdatedDocumentsDisplaying={toggleUpdatedDocumentsDisplaying}
                    updatedInPromoMats={
                      updatedInPromoMatsEntities.contains(EntityType.MODULE_BUNDLE)
                    }
                    dataTestId={`${assetsWrapperDataTestId}-moduleBundleList`}
                  />
                }
                {
                  this.shouldDisplayAsset(AssetFilter.TEXT_COLLECTIONS) &&
                  <TextCollectionList
                    items={textCollections}
                    showUpdatedTextCollections={showUpdatedTextCollections}
                    toggleUpdatedDocumentsDisplaying={toggleUpdatedDocumentsDisplaying}
                    updatedInPromoMats={updatedInPromoMatsEntities.contains(EntityType.TEXT_COLLECTION)}
                    dataTestId={`${assetsWrapperDataTestId}-textCollectionList`}
                  />
                }
                {
                  this.shouldDisplayAsset(AssetFilter.STORY_CARDS) &&
                  <StoryCardList
                    items={storyCardsAndMagicForms}
                    showUpdatedStoryCards={showUpdatedStoryCards}
                    toggleUpdatedDocumentsDisplaying={toggleUpdatedDocumentsDisplaying}
                    updatedInPromoMats={
                      updatedInPromoMatsEntities.contains(EntityType.STORY_CARD) ||
                      updatedInPromoMatsEntities.contains(EntityType.MAGIC_FORM)
                    }
                    dataTestId={`${assetsWrapperDataTestId}-storyCardList`}
                  />
                }
                {
                  (this.shouldDisplayAsset(AssetFilter.LAYOUTS) || this.shouldDisplayAsset(AssetFilter.GROUP_LAYOUTS)) && !isReadOnly &&
                  <ReusableLayoutList
                    reusableLayouts={this.getLayoutsToShow()}
                    updatedInPromoMats={updatedInPromoMatsEntities.contains(EntityType.LAYOUT)}
                    isExpand={activeSections.has(ProjectPanelSection.REUSABLE_LAYOUTS)}
                    isReadOnly={isReadOnly}
                    toggleExpand={toggleSection.bind(null, ProjectPanelSection.REUSABLE_LAYOUTS)}
                    dataTestId={`${assetsWrapperDataTestId}-reusableLayoutList`}
                  />
                }
                {
                  this.shouldDisplayAsset(AssetFilter.IMAGES) && !isReadOnly &&
                  <ImageList
                    showHeader={true}
                    documents={images}
                    updatedInPromoMats={updatedInPromoMatsEntities.contains(EntityType.IMAGE)}
                    isExpand={activeSections.has(ProjectPanelSection.IMAGES)}
                    toggleExpand={toggleSection.bind(null, ProjectPanelSection.IMAGES)}
                    dataTestId={`${assetsWrapperDataTestId}-imageList`}
                  />
                }
                {
                  this.shouldDisplayAsset(AssetFilter.REFERENCES) && !isReadOnly &&
                  <CitationList
                    citations={citations}
                    updatedInPromoMats={updatedInPromoMatsEntities.contains(EntityType.REFERENCE_CITATION)}
                    isExpand={activeSections.has(ProjectPanelSection.REFERENCE_CITATIONS)}
                    toggleExpand={toggleSection.bind(null, ProjectPanelSection.REFERENCE_CITATIONS)}
                    dataTestId={`${assetsWrapperDataTestId}-citationList`}
                  />
                }
                {
                  this.shouldDisplayAsset(AssetFilter.TEXT) && !isReadOnly &&
                  <TextComponentList
                    showHeader={true}
                    components={textComponents}
                    isExpand={activeSections.has(ProjectPanelSection.TEXTS)}
                    toggleExpand={toggleSection.bind(null, ProjectPanelSection.TEXTS)}
                    dataTestId={`${assetsWrapperDataTestId}-textComponentList`}
                  />
                }
              </div>
            </>
          }
          {
            <div className={classNames(Styles.layout__wrapper, { [Styles.show]: activeTab === ProjectPanelTab.ELEMENTS })}
              data-testid="projectPanel-listsContainer-elements"
            >
              <LayoutListContainer />
              <ElementList />
              <InteractiveElements />
            </div>
          }
        </div>
        <RefreshPanel />
      </div>
    );
  }
}
